// Borrower support — staff side.
//
// WHY THIS IS SEPARATE FROM chat.service.ts:
// The team-chat tables (chat_conversations / chat_participants / chat_messages)
// key on `profiles`. Borrowers deliberately have NO profiles row — `bapp_is_staff()`
// is defined as "has a profiles row", so giving a borrower one would grant them
// staff-level RLS across every borrower's loans and personal data. Borrower
// threads therefore live in `bapp_messages`, scoped by `user_id`.
//
// A conversation is (borrower <-> ONE agent). Staff see only the conversations
// where they are that agent — being staff is NOT sufficient, which is what the
// old branch-wide policy got wrong. RLS also pins staff inserts to
// sender='agent', so nobody can forge a message that looks like the borrower's.
// Schema + policies: supabase/borrower_support_conversations.sql
import { supabase } from '../../lib/supabase';

export interface BorrowerThread {
  conversation_id: string;
  user_id: string;
  agent_id: string | null;
  branch_id: string | null;
  branch_name: string | null;
  /** Retained because the RPC still returns it; the UI no longer closes
   *  threads. A "closed" thread read to the borrower like one staff could
   *  still answer, so they waited forever. See db/chat-delete-and-unread.sql. */
  status: 'open' | 'closed';
  last_message_at: string | null;
  borrower_msgs: number;
  last_body: string | null;
  loandisk_borrower_id: string | null;
  unique_number: string | null;
  full_name: string | null;
}

export interface BorrowerMessage {
  id: string;
  conversation_id: string;
  user_id: string;
  sender: 'borrower' | 'agent' | 'bot';
  body: string;
  created_at: string;
}

/**
 * Conversations ASSIGNED TO THIS STAFF MEMBER, newest first.
 *
 * Scoped to `agent_id = auth.uid()` — not to the branch. Branch scoping still
 * meant every colleague read every borrower's messages, which is the bug this
 * replaced. A borrower who picks someone else gets a separate conversation that
 * is invisible here.
 */
export async function listBorrowerThreads(): Promise<BorrowerThread[]> {
  const { data, error } = await supabase.rpc('bapp_support_threads_for_me');
  if (error) throw error;
  return (data ?? []) as BorrowerThread[];
}

/** One conversation, oldest first. RLS returns nothing unless it's yours. */
export async function loadBorrowerThread(conversationId: string): Promise<BorrowerMessage[]> {
  const { data, error } = await supabase
    .from('bapp_messages')
    .select('id, conversation_id, user_id, sender, body, created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as BorrowerMessage[];
}

/** Reply as staff. RLS requires you to be THIS conversation's agent and the
 *  conversation to be open, and pins sender to 'agent'. */
export async function replyToBorrower(t: BorrowerThread, body: string): Promise<void> {
  const text = body.trim();
  if (!text) return;
  const { error } = await supabase.from('bapp_messages').insert({
    conversation_id: t.conversation_id,
    user_id: t.user_id,
    sender: 'agent',
    body: text,
  });
  if (error) throw error;
}

/**
 * Unread borrower messages per conversation, for THIS agent.
 *
 * There was previously no staff-side read position at all, so borrower replies
 * never reached the message badge — a borrower could sit unanswered while the
 * launcher showed nothing. Counted against `bapp_conversation_reads`; see
 * db/chat-delete-and-unread.sql in the borrower PWA repo.
 */
export async function listBorrowerUnread(): Promise<Record<string, number>> {
  const { data, error } = await supabase.rpc('bapp_support_unread_for_me');
  if (error) throw error;
  const out: Record<string, number> = {};
  for (const row of (data ?? []) as { conversation_id: string; unread: number }[]) {
    out[row.conversation_id] = Number(row.unread) || 0;
  }
  return out;
}

/** Moves this agent's read position to now. Call when a thread is opened. */
export async function markBorrowerThreadRead(conversationId: string): Promise<void> {
  const { error } = await supabase.rpc('bapp_mark_conversation_read', { conv: conversationId });
  if (error) throw error;
}

/** Clears the whole borrower inbox badge in one go. */
export async function markAllBorrowerRead(): Promise<void> {
  const { error } = await supabase.rpc('bapp_mark_all_support_read');
  if (error) throw error;
}

/**
 * Presence heartbeat. The borrower app shows staff as Online/Away from
 * `bapp_staff_presence`; without this nobody is ever online and support routing
 * falls back to "the branch manager". Call once a minute while signed in.
 */
export async function sendHeartbeat(): Promise<void> {
  await supabase.rpc('bapp_heartbeat');
}
